import { Link, createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import { ActivityFeed } from "@/components/ActivityFeed.tsx";
import { ConfirmModal } from "@/components/ConfirmModal.tsx";
import { CopyButton } from "@/components/CopyButton.tsx";
import { type SettingsCardPatchResponse, SettingsCard } from "@/components/SettingsCard.tsx";
import { Sparkline } from "@/components/Sparkline.tsx";
import { TopReferers } from "@/components/TopReferers.tsx";
import { VersionBars } from "@/components/VersionBars.tsx";
import { EyeOff, Lock, Pencil, Trash2 } from "lucide-react";
import { type AnalyticsRange, useFileAnalytics } from "@/lib/analytics.ts";
import { ApiError, UnauthorizedError, apiDelete, apiGet, apiPatch } from "@/lib/api.ts";
import { absoluteTime, humanBytes, humanCount, relativeTime } from "@/lib/format.ts";
import { useVisitCount } from "@/lib/visitCount.ts";

type FileVersion = {
  versionNumber: number;
  createdAt: string;
  sizeBytes: number;
  entryPath: string;
  isBundle: boolean;
  // FRE-283: optional free-text label (e.g. "draft", "approved"). Rendered
  // inline next to the version number when non-empty; absent versions render
  // just "v3".
  versionLabel: string | null;
};

// FRE-283: shared cap between the inline edit input and the worker's
// normalizeVersionLabel. Keep this in sync with MAX_VERSION_LABEL_LEN in
// `apps/worker/src/files/service.ts`.
const MAX_VERSION_LABEL_LEN = 100;

type FileDetail = {
  identifier: string;
  slug: string;
  nanoid: string;
  title: string | null;
  scope: "org" | "private";
  urlAccess: "public" | "password" | "org";
  version: number;
  hostedUrl: string;
  rawUrl: string;
  versionedHostedUrl: string;
  versionedRawUrl: string;
  createdAt: string;
  updatedAt: string;
  createdByUserId?: string | null;
  versions: FileVersion[];
};

async function fetchFile(identifier: string): Promise<FileDetail> {
  return apiGet<FileDetail>(`/api/v1/files/${encodeURIComponent(identifier)}`);
}

// Build a per-version URL by injecting `@vN` into the slug-or-nanoid segment of
// hostedUrl/rawUrl. The unversioned URLs always end in `…/<slug-or-nanoid>` (or
// `…/<slug-or-nanoid>/path` for bundles). We append `@vN` to the first segment
// after the `/f/` or `/r/` prefix. Falls back to suffix append if shape is
// unexpected — keeps display non-fatal for odd URLs.
function versionedUrl(base: string, version: number): string {
  try {
    const url = new URL(base);
    const segments = url.pathname.split("/").filter(Boolean);
    if (segments.length >= 2 && (segments[0] === "f" || segments[0] === "r")) {
      const idx = 1;
      const current = segments[idx];
      if (current && !current.includes("@v")) {
        segments[idx] = `${current}@v${version}`;
        url.pathname = `/${segments.join("/")}`;
        return url.toString();
      }
    }
    return base;
  } catch {
    return base;
  }
}

export const Route = createFileRoute("/files/$identifier")({
  beforeLoad: async ({ params }) => {
    // Cheap auth gate to mirror the list view. The detail fetch happens in the
    // component so we can render a friendly 404 inline rather than a router
    // error boundary.
    try {
      await apiGet<{ userId: string }>("/api/v1/me");
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        throw redirect({
          to: "/login",
          search: { next: `/files/${params.identifier}` },
        });
      }
      throw err;
    }
  },
  component: FileDetailComponent,
});

// FRE-283: per-row inline edit for `versionLabel`. Three visual states:
//   1. idle  → label text + pencil icon (or just the pencil if label is empty)
//   2. edit  → text input + Save/Cancel buttons
//   3. busy  → input disabled, Save shows "Saving…"
//
// Inline validation: max 100 chars; error rendered under the input. Cancel
// always reverts to the last persisted value. On success we hand the new
// label back to the parent so it can patch its local versions[] state.
function VersionLabelEditor({
  identifier,
  versionNumber,
  initialLabel,
  onSaved,
}: {
  identifier: string;
  versionNumber: number;
  initialLabel: string | null;
  onSaved: (next: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(initialLabel ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEdit = () => {
    setDraft(initialLabel ?? "");
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    if (busy) return;
    setDraft(initialLabel ?? "");
    setError(null);
    setEditing(false);
  };

  const save = async () => {
    if (busy) return;
    const trimmed = draft.trim();
    if (trimmed.length > MAX_VERSION_LABEL_LEN) {
      setError(`Label must be at most ${MAX_VERSION_LABEL_LEN} characters`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Empty → null mirrors server-side normalisation (column accepts NULL).
      const nextValue = trimmed.length > 0 ? trimmed : null;
      const res = await apiPatch<{ versionLabel: string | null }>(
        `/api/v1/files/${encodeURIComponent(identifier)}/versions/${versionNumber}`,
        { versionLabel: nextValue },
      );
      // Defensive: trust the server's normalised value over our local draft.
      onSaved(res.versionLabel);
      setEditing(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save label";
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <span className="inline-flex items-center gap-1">
        {initialLabel ? <span className="text-sm text-foreground/80">— {initialLabel}</span> : null}
        <button
          type="button"
          onClick={startEdit}
          aria-label={
            initialLabel
              ? `Edit label for version ${versionNumber}`
              : `Add label for version ${versionNumber}`
          }
          title={initialLabel ? "Edit label" : "Add label"}
          className="inline-flex items-center rounded p-0.5 text-muted-foreground/60 hover:bg-muted hover:text-foreground/80"
        >
          <Pencil className="size-4" />
        </button>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void save();
          } else if (e.key === "Escape") {
            e.preventDefault();
            cancel();
          }
        }}
        placeholder="e.g. approved"
        aria-label={`Label for version ${versionNumber}`}
        maxLength={MAX_VERSION_LABEL_LEN + 10}
        disabled={busy}
        // Auto-focus on edit. autoFocus is fine here — the input only renders
        // after a user-driven state change.
        // biome-ignore lint/a11y/noAutofocus: explicit user-initiated edit
        autoFocus
        className="rounded-md border border-input bg-card px-2 py-0.5 text-sm text-foreground focus:border-input focus:outline-none disabled:bg-muted"
      />
      <button
        type="button"
        onClick={() => void save()}
        disabled={busy}
        className="inline-flex items-center rounded-md bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy ? "Saving…" : "Save"}
      </button>
      <button
        type="button"
        onClick={cancel}
        disabled={busy}
        className="inline-flex items-center rounded-md border border-input px-2 py-0.5 text-xs font-medium text-foreground/80 hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Cancel
      </button>
      {error ? (
        <span role="alert" className="text-xs text-destructive">
          {error}
        </span>
      ) : null}
    </span>
  );
}

function ScopePill({ scope }: { scope: FileDetail["scope"] }) {
  if (scope === "private") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-input bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground/80">
        <EyeOff className="size-4" />
        private
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-input bg-card px-2.5 py-0.5 text-xs font-medium text-foreground/80">
      org
    </span>
  );
}

function AccessPill({ access }: { access: FileDetail["urlAccess"] }) {
  if (access === "password") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-input bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground/80">
        <Lock className="size-4" />
        password
      </span>
    );
  }
  if (access === "org") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-input bg-muted px-2.5 py-0.5 text-xs font-medium text-foreground/80">
        <Lock className="size-4" />
        org only
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-input bg-card px-2.5 py-0.5 text-xs font-medium text-foreground/80">
      public
    </span>
  );
}

// Lifetime visit count rendered as a single block at the top of the detail
// header. Reuses the shared in-memory cache via useVisitCount so click-through
// from the list view paints instantly when the orchestrator has already filled
// the count. Errors degrade silently to "—" — visit counts are decorative
// rather than load-bearing for this page.
function VisitCountStat({ identifier }: { identifier: string }) {
  const { count, loading, error } = useVisitCount(identifier);
  const display = count !== null ? humanCount(count) : loading ? "…" : "—";
  const suffix = count === 1 ? "visit" : "visits";
  return (
    <div
      className="rounded-md border border-border bg-muted/40 px-3 py-2"
      title={
        count !== null
          ? `${count} confident-human ${suffix} since file creation`
          : error
            ? "Visit count unavailable"
            : undefined
      }
    >
      <div className="flex items-baseline gap-2">
        <span className="text-lg font-semibold tabular-nums text-foreground">{display}</span>
        <span className="text-xs text-muted-foreground">{suffix}</span>
      </div>
      <div className="mt-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
        lifetime
      </div>
    </div>
  );
}

const ANALYTICS_RANGES: { value: AnalyticsRange; label: string }[] = [
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
  { value: "all", label: "All" },
];

// Analytics card on the file detail page (FRE-276/277/278): sparkline + per-
// version bars + top referrers, all driven off a single `/analytics?range=…`
// fetch with a client-side range switcher. Defaults to `30d`. Loading paints
// a skeleton; errors surface inline with a retry button so the surrounding
// page stays interactive.
function AnalyticsCard({
  identifier,
  currentVersionNumber,
}: {
  identifier: string;
  currentVersionNumber: number;
}) {
  const [range, setRange] = useState<AnalyticsRange>("30d");
  const { data, loading, error, refetch } = useFileAnalytics(identifier, range);

  return (
    <section className="rounded-md border border-border bg-card px-6 py-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Analytics</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Confident-human visits over the selected range.
          </p>
        </div>
        <div
          role="tablist"
          aria-label="Analytics range"
          className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 p-0.5"
        >
          {ANALYTICS_RANGES.map((r) => {
            const active = r.value === range;
            return (
              <button
                key={r.value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setRange(r.value)}
                className={`rounded px-2 py-0.5 text-xs font-medium ${
                  active
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {r.label}
              </button>
            );
          })}
        </div>
      </div>

      {error && !data ? (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <span>Couldn't load analytics: {error}</span>
          <button
            type="button"
            onClick={refetch}
            className="rounded-md border border-destructive/40 px-2 py-0.5 text-[11px] font-medium text-destructive hover:bg-destructive/20"
          >
            Retry
          </button>
        </div>
      ) : null}

      {/* Sparkline */}
      <div className="mt-4">
        {loading && !data ? (
          <div className="h-[120px] w-full animate-pulse rounded-md bg-muted" />
        ) : data ? (
          <Sparkline
            data={data.byDay}
            ariaLabel={`Visits over ${range === "all" ? "all time" : `the last ${range}`}, total ${data.totalVisits}`}
          />
        ) : null}
      </div>

      {/* Two-column grid: per-version + top referrers */}
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Visits by version
          </h3>
          <div className="mt-2">
            {loading && !data ? (
              <div className="space-y-2">
                <div className="h-4 w-full animate-pulse rounded bg-muted" />
                <div className="h-4 w-full animate-pulse rounded bg-muted" />
                <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
              </div>
            ) : data ? (
              <VersionBars byVersion={data.byVersion} currentVersionNumber={currentVersionNumber} />
            ) : null}
          </div>
        </div>
        <div>
          {/* Section hidden when there are no referrers after host-grouping. */}
          {data && data.topReferers.length > 0 ? (
            <>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Top Referrers
              </h3>
              <div className="mt-2">
                <TopReferers topReferers={data.topReferers} />
              </div>
            </>
          ) : loading && !data ? (
            <>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Top Referrers
              </h3>
              <div className="mt-2 space-y-2">
                <div className="h-4 w-full animate-pulse rounded bg-muted" />
                <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
              </div>
            </>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function UrlRow({ label, url, copyLabel }: { label: string; url: string; copyLabel: string }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="w-20 shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="flex-1 min-w-0">
        <a
          href={url}
          target="_blank"
          rel="noreferrer noopener"
          className="block truncate font-mono text-xs text-foreground/80 hover:text-foreground hover:underline"
        >
          {url}
        </a>
      </div>
      <CopyButton value={url} label={copyLabel} />
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="h-4 w-20 rounded bg-muted animate-pulse" />
      <div className="rounded-md border border-border bg-card p-6 space-y-4">
        <div className="h-6 w-2/3 rounded bg-muted animate-pulse" />
        <div className="h-4 w-1/3 rounded bg-muted animate-pulse" />
      </div>
      <div className="rounded-md border border-border bg-card p-6 space-y-3">
        <div className="h-4 w-1/4 rounded bg-muted animate-pulse" />
        <div className="h-4 w-full rounded bg-muted animate-pulse" />
        <div className="h-4 w-full rounded bg-muted animate-pulse" />
      </div>
      <div className="rounded-md border border-border bg-card p-6 space-y-3">
        <div className="h-4 w-1/4 rounded bg-muted animate-pulse" />
        <div className="h-12 w-full rounded bg-muted animate-pulse" />
        <div className="h-12 w-full rounded bg-muted animate-pulse" />
      </div>
    </div>
  );
}

function NotFoundView({ identifier }: { identifier: string }) {
  return (
    <div className="max-w-2xl mx-auto">
      <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
        ← All files
      </Link>
      <div className="mt-4 rounded-md border border-border bg-card px-6 py-12 text-center">
        <h1 className="text-lg font-semibold text-foreground">Not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          No file with identifier <code className="font-mono">{identifier}</code> in your scope.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Back to files
        </Link>
      </div>
    </div>
  );
}

function FileDetailComponent() {
  const { identifier } = Route.useParams();
  const navigate = useNavigate();
  const [file, setFile] = useState<FileDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pull lifetime per-version counts for the version history table (FRE-277).
  // Same `range=all` endpoint as the visit count stat — the analytics cache
  // dedupes the actual fetch, so there's no second network round-trip.
  const { data: lifetimeAnalytics } = useFileAnalytics(identifier, "all");
  const visitsByVersion = new Map<number, number>(
    lifetimeAnalytics?.byVersion.map((v) => [v.versionNumber, v.count]) ?? [],
  );

  // Delete-file modal: simple boolean — no optimistic state since we navigate
  // away on success. Busy flag locks the modal while the request is in flight.
  const [deleteFileOpen, setDeleteFileOpen] = useState(false);
  const [deletingFile, setDeletingFile] = useState(false);

  // Delete-version: tracks which version (if any) is pending confirmation,
  // and which is currently being deleted. We single-flight on the latter so
  // a second click can't fire a parallel DELETE.
  const [pendingDeleteVersion, setPendingDeleteVersion] = useState<number | null>(null);
  const [deletingVersion, setDeletingVersion] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const data = await fetchFile(identifier);
      setFile(data);
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        await navigate({ to: "/login", search: { next: `/files/${identifier}` } });
        return;
      }
      if (err instanceof ApiError && err.status === 404) {
        setNotFound(true);
        return;
      }
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to load file";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [identifier, navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  // File delete — no optimistic state. On success we navigate to "/" and the
  // route is unmounted; on error we close the modal and surface a banner.
  const handleConfirmDeleteFile = useCallback(async () => {
    if (deletingFile) return;
    setDeletingFile(true);
    setError(null);
    try {
      await apiDelete(`/api/v1/files/${encodeURIComponent(identifier)}`);
      await navigate({ to: "/" });
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        await navigate({ to: "/login", search: { next: `/files/${identifier}` } });
        return;
      }
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Failed to delete file";
      setError(message);
      setDeleteFileOpen(false);
    } finally {
      setDeletingFile(false);
    }
  }, [deletingFile, identifier, navigate]);

  // Version delete with optimistic removal. We snapshot the prior versions
  // array so we can roll back on error. On success we re-fetch the detail
  // because the server may have re-pointed `current_version_id` (and thus
  // the URLs) to the next-most-recent version. If the file 404s after the
  // delete it means the server auto-soft-deleted because the last version
  // is gone — navigate home.
  const handleConfirmDeleteVersion = useCallback(
    async (versionNumber: number) => {
      if (deletingVersion !== null) return;
      const current = file;
      if (!current) return;
      const snapshot = current.versions;
      setDeletingVersion(versionNumber);
      setError(null);
      // Optimistic removal.
      setFile({
        ...current,
        versions: snapshot.filter((v) => v.versionNumber !== versionNumber),
      });
      try {
        await apiDelete(
          `/api/v1/files/${encodeURIComponent(identifier)}/versions/${versionNumber}`,
        );
        try {
          const fresh = await fetchFile(identifier);
          setFile(fresh);
        } catch (fetchErr) {
          if (fetchErr instanceof ApiError && fetchErr.status === 404) {
            // Last version removed → file auto-soft-deleted server-side.
            await navigate({ to: "/" });
            return;
          }
          throw fetchErr;
        }
        setPendingDeleteVersion(null);
      } catch (err) {
        // Rollback optimistic removal.
        setFile((prev) => (prev ? { ...prev, versions: snapshot } : prev));
        if (err instanceof UnauthorizedError) {
          await navigate({ to: "/login", search: { next: `/files/${identifier}` } });
          return;
        }
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Failed to delete version";
        setError(message);
        setPendingDeleteVersion(null);
      } finally {
        setDeletingVersion(null);
      }
    },
    [deletingVersion, file, identifier, navigate],
  );

  if (loading) return <DetailSkeleton />;
  if (notFound) return <NotFoundView identifier={identifier} />;

  if (error && !file) {
    return (
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
          ← All files
        </Link>
        <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive flex items-center justify-between gap-3">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => void load()}
            className="rounded-md border border-destructive/40 px-2.5 py-1 text-xs font-medium text-destructive hover:bg-destructive/20"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!file) return null;

  // Merge the PATCH response into the existing detail state. The server returns
  // updated metadata + freshly-built URLs but not the versions array, so we
  // preserve the existing version history (the PATCH endpoint never mutates
  // versions). If the identifier changed (slug rename), replace the URL so
  // the address bar reflects the new identifier — old URL still works via 301
  // server-side.
  const handleSettingsUpdate = (updated: SettingsCardPatchResponse) => {
    setFile((prev) =>
      prev
        ? {
            ...prev,
            ...updated,
          }
        : prev,
    );
    if (updated.identifier !== identifier) {
      void navigate({
        to: "/files/$identifier",
        params: { identifier: updated.identifier },
        replace: true,
      });
    }
  };

  const titleNode = file.title ? (
    <span>{file.title}</span>
  ) : (
    <span className="italic text-foreground/80">{file.slug}</span>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
        ← All files
      </Link>

      {error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive flex items-center justify-between gap-3">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => void load()}
            className="rounded-md border border-destructive/40 px-2.5 py-1 text-xs font-medium text-destructive hover:bg-destructive/20"
          >
            Retry
          </button>
        </div>
      ) : null}

      {/* Header */}
      <div className="rounded-md border border-border bg-card px-6 py-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-foreground truncate">{titleNode}</h1>
            <div className="mt-1 font-mono text-sm text-muted-foreground truncate">
              {file.identifier}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <ScopePill scope={file.scope} />
            <AccessPill access={file.urlAccess} />
            <span className="inline-flex items-center rounded-full border border-input bg-card px-2.5 py-0.5 text-xs font-mono text-foreground/80">
              v{file.version}
            </span>
            <button
              type="button"
              onClick={() => setDeleteFileOpen(true)}
              disabled={deletingFile}
              aria-label="Delete file"
              title="Delete file"
              className="inline-flex items-center gap-1 rounded-md border border-input px-2 py-1 text-xs font-medium text-destructive hover:bg-destructive/10 hover:border-destructive/40 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Trash2 className="size-4" />
              Delete file
            </button>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <VisitCountStat identifier={file.identifier} />
        </div>
        <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
          <div className="flex gap-2">
            <dt className="text-muted-foreground">Slug</dt>
            <dd className="font-mono text-foreground/80 truncate">{file.slug}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="text-muted-foreground">nanoid</dt>
            <dd className="font-mono text-foreground/80 truncate">{file.nanoid}</dd>
          </div>
        </dl>
      </div>

      {/* Settings (collapsible) */}
      <SettingsCard
        file={{
          identifier: file.identifier,
          slug: file.slug,
          nanoid: file.nanoid,
          title: file.title,
          scope: file.scope,
          urlAccess: file.urlAccess,
        }}
        onUpdate={handleSettingsUpdate}
      />

      {/* Analytics (sparkline + per-version + top referrers) */}
      <AnalyticsCard identifier={file.identifier} currentVersionNumber={file.version} />

      {/* Current URLs */}
      <section className="rounded-md border border-border bg-card px-6 py-5">
        <h2 className="text-sm font-semibold text-foreground">Current URLs</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Unversioned URLs always resolve to the current version (v{file.version}).
        </p>
        <div className="mt-3 divide-y divide-border/60">
          <UrlRow label="Hosted" url={file.hostedUrl} copyLabel="Copy hosted" />
          <UrlRow label="Raw" url={file.rawUrl} copyLabel="Copy raw" />
          <UrlRow
            label={`Hosted @v${file.version}`}
            url={file.versionedHostedUrl}
            copyLabel="Copy"
          />
          <UrlRow label={`Raw @v${file.version}`} url={file.versionedRawUrl} copyLabel="Copy" />
        </div>
      </section>

      {/* Version history */}
      <section className="rounded-md border border-border bg-card overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground">Version history</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {file.versions.length} version{file.versions.length === 1 ? "" : "s"}.
          </p>
        </div>
        {file.versions.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-muted-foreground">
            No version history available.
          </div>
        ) : (
          <ul className="divide-y divide-border/60">
            {[...file.versions]
              .sort((a, b) => b.versionNumber - a.versionNumber)
              .map((v) => {
                const isCurrent = v.versionNumber === file.version;
                const vHosted = versionedUrl(file.hostedUrl, v.versionNumber);
                const vRaw = versionedUrl(file.rawUrl, v.versionNumber);
                return (
                  <li
                    key={v.versionNumber}
                    className={`px-6 py-4 ${isCurrent ? "bg-amber-50/40" : ""}`}
                  >
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-mono text-sm font-medium text-foreground">
                          v{v.versionNumber}
                        </span>
                        {/* FRE-283: inline label edit. Renders `— approved`
                            when set; just the pencil otherwise. */}
                        <VersionLabelEditor
                          identifier={file.identifier}
                          versionNumber={v.versionNumber}
                          initialLabel={v.versionLabel}
                          onSaved={(nextLabel) =>
                            setFile((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    versions: prev.versions.map((row) =>
                                      row.versionNumber === v.versionNumber
                                        ? { ...row, versionLabel: nextLabel }
                                        : row,
                                    ),
                                  }
                                : prev,
                            )
                          }
                        />
                        {isCurrent ? (
                          <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                            current
                          </span>
                        ) : null}
                        {v.isBundle ? (
                          <span className="inline-flex items-center rounded-full border border-input bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                            bundle
                          </span>
                        ) : null}
                      </div>
                      <div
                        className="text-xs text-muted-foreground shrink-0"
                        title={absoluteTime(v.createdAt)}
                      >
                        {relativeTime(v.createdAt)}
                      </div>
                      <div className="text-xs text-muted-foreground shrink-0">
                        {humanBytes(v.sizeBytes)}
                      </div>
                      <div
                        className={`text-xs shrink-0 tabular-nums ${
                          isCurrent ? "font-semibold text-amber-800" : "text-muted-foreground"
                        }`}
                        title={
                          lifetimeAnalytics
                            ? `${visitsByVersion.get(v.versionNumber) ?? 0} confident-human visits to v${v.versionNumber}`
                            : "Loading visit count…"
                        }
                      >
                        {(() => {
                          const count = visitsByVersion.get(v.versionNumber) ?? 0;
                          return `${count} visit${count === 1 ? "" : "s"}`;
                        })()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-xs text-muted-foreground truncate">
                          {v.entryPath}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPendingDeleteVersion(v.versionNumber)}
                        disabled={deletingVersion !== null}
                        aria-label={`Delete version ${v.versionNumber}`}
                        title={`Delete v${v.versionNumber}`}
                        className="inline-flex items-center gap-1 rounded-md border border-input px-1.5 py-0.5 text-[11px] font-medium text-destructive hover:bg-destructive/10 hover:border-destructive/40 shrink-0 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Trash2 className="size-4" />
                        Delete
                      </button>
                    </div>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground w-12 shrink-0">
                          Hosted
                        </span>
                        <a
                          href={vHosted}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="flex-1 truncate font-mono text-xs text-foreground/80 hover:text-foreground hover:underline"
                        >
                          {vHosted}
                        </a>
                        <CopyButton value={vHosted} label="Copy" size="xs" />
                      </div>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground w-12 shrink-0">
                          Raw
                        </span>
                        <a
                          href={vRaw}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="flex-1 truncate font-mono text-xs text-foreground/80 hover:text-foreground hover:underline"
                        >
                          {vRaw}
                        </a>
                        <CopyButton value={vRaw} label="Copy" size="xs" />
                      </div>
                    </div>
                  </li>
                );
              })}
          </ul>
        )}
      </section>

      {/* FRE-284: Activity feed (audit log). Placed below Version history so
          the timeline reads top→down: what the file is, what it looks like,
          how it changed. */}
      <ActivityFeed identifier={file.identifier} />

      {/* Metadata footer */}
      <section className="rounded-md border border-border bg-card px-6 py-4">
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3 text-xs">
          <div>
            <dt className="text-muted-foreground">Created</dt>
            <dd className="mt-0.5 text-foreground/80" title={absoluteTime(file.createdAt)}>
              {relativeTime(file.createdAt)}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Updated</dt>
            <dd className="mt-0.5 text-foreground/80" title={absoluteTime(file.updatedAt)}>
              {relativeTime(file.updatedAt)}
            </dd>
          </div>
          {file.createdByUserId ? (
            <div className="min-w-0">
              <dt className="text-muted-foreground">Created by</dt>
              <dd className="mt-0.5 font-mono text-foreground/80 truncate">
                {file.createdByUserId}
              </dd>
            </div>
          ) : null}
        </dl>
      </section>

      <ConfirmModal
        open={deleteFileOpen}
        title="Delete file?"
        body={
          <p>
            This file will be hidden but versions remain accessible at their{" "}
            <code className="font-mono">@vN</code> URLs until permanently deleted later.
          </p>
        }
        confirmLabel="Delete file"
        danger
        busy={deletingFile}
        onConfirm={() => void handleConfirmDeleteFile()}
        onCancel={() => {
          if (!deletingFile) setDeleteFileOpen(false);
        }}
      />

      <ConfirmModal
        open={pendingDeleteVersion !== null}
        title={
          pendingDeleteVersion !== null
            ? `Delete version v${pendingDeleteVersion}?`
            : "Delete version?"
        }
        body={
          pendingDeleteVersion !== null ? (
            file.versions.length <= 1 ? (
              <p>
                This is the only remaining version. Deleting{" "}
                <span className="font-mono">v{pendingDeleteVersion}</span> will hide the file
                entirely. Older URLs pointing at{" "}
                <span className="font-mono">@v{pendingDeleteVersion}</span> will return 404.
              </p>
            ) : (
              <p>
                Version <span className="font-mono">v{pendingDeleteVersion}</span> will be hidden.
                Older URLs pointing at <span className="font-mono">@v{pendingDeleteVersion}</span>{" "}
                will return 404.
              </p>
            )
          ) : null
        }
        confirmLabel="Delete version"
        danger
        busy={deletingVersion !== null}
        onConfirm={() => {
          if (pendingDeleteVersion !== null) {
            void handleConfirmDeleteVersion(pendingDeleteVersion);
          }
        }}
        onCancel={() => {
          if (deletingVersion === null) setPendingDeleteVersion(null);
        }}
      />
    </div>
  );
}
